import { getRawQuery } from '@/action/supabase';
import { getStorage, setStorage } from '@/utils/util_storage';
import type { Announcement, AnnouncementCopy } from '@/utils/util_types';

export const ANNOUNCEMENT_READ_KEY = 'aispg_read_announcements_v1';
const sessionReadIds = new Set<string>();

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseCopy(value: unknown): AnnouncementCopy | null {
    if (!isRecord(value) || typeof value.title !== 'string' || !value.title.trim()
        || typeof value.body !== 'string' || !value.body.trim()) return null;
    return { title: value.title.trim(), body: value.body.trim() };
}

export function parseAnnouncement(value: unknown, now = Date.now()): Announcement | null {
    const copy = parseCopy(value);
    if (!copy || !isRecord(value) || value.enabled !== true
        || typeof value.id !== 'string' || !value.id.trim()) return null;
    for (const field of ['starts_at', 'ends_at'] as const) {
        if (value[field] == null) continue;
        if (typeof value[field] !== 'string' || !Number.isFinite(Date.parse(value[field]))) return null;
        const date = Date.parse(value[field]);
        if (field === 'starts_at' ? now < date : now >= date) return null;
    }
    const translations: Record<string, AnnouncementCopy> = {};
    if (isRecord(value.translations)) {
        for (const [language, content] of Object.entries(value.translations)) {
            const translated = parseCopy(content);
            if (translated) translations[language] = translated;
        }
    }
    return { ...copy, id: value.id.trim(), enabled: true, translations };
}

async function readAnnouncementIds(): Promise<Set<string>> {
    let ids: unknown;
    try { ids = JSON.parse(await getStorage(ANNOUNCEMENT_READ_KEY) ?? '[]'); }
    catch { ids = []; }
    return new Set([
        ...(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []),
        ...sessionReadIds,
    ]);
}

export async function markAnnouncementRead(id: string): Promise<void> {
    sessionReadIds.add(id);
    await setStorage(ANNOUNCEMENT_READ_KEY, JSON.stringify([...(await readAnnouncementIds())]));
}

export async function fetchUnreadAnnouncement(signal: AbortSignal): Promise<Announcement | null> {
    const [{ data, error }, readIds] = await Promise.all([
        getRawQuery('configs', 'value').eq('id', 'announcement').abortSignal(signal).returns<{ value: unknown }[]>(),
        readAnnouncementIds(),
    ]);
    if (error) throw error;
    const announcement = parseAnnouncement(data?.[0]?.value);
    return announcement && !readIds.has(announcement.id) ? announcement : null;
}
